import { css } from '@emotion/react';
import { useCallback } from 'react';
import { Accordion } from '~/_shared/baseComponents/accordion';
import { Inline } from '~/_shared/baseComponents/layout/inline.component';
import { ToggleComponent } from '~/_shared/baseComponents/toggle';
import { type Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import { isNullsy } from '~/_shared/utils/typeGuards';
import { type DemographicsRegion } from '~/store/demographics/demographics.repository';
import { type SpreadsheetColumn } from '../../types/spreadsheetData/spreadsheetColumn';
import { ModalComponent } from '../modal/modal.component';
import { OverlayLoaderComponent } from '../overlay/overlayLoader.component';
import { CustomizeMetricsResultsComponent } from './results/customizeMetricsResults.component';
import { CustomizeMetricsSelectDataComponent } from './selectData/customizeMetricsSelectData.component';
import { type SelectedDataMetric } from './selectedDataMetric.type';

type CustomizeMetricsProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  selectedData: ReadonlyArray<SelectedDataMetric>;
  onSelectedDataChange: (newData: ReadonlyArray<SelectedDataMetric>) => void;
  addSelectionButtonText: string;
  selectedDataCaption: string;
  columnDataSources: ReadonlyArray<SpreadsheetColumn>;
  demographicsRegions: ReadonlyArray<DemographicsRegion>;
  title: string;
  hideFillMetrics?: boolean;
  onToggleHideFillMetrics?: (isOn: boolean) => void;
}>;

export const customizeMetricsDropdownTriggerStyle = ({ isPlaceholder }: { isPlaceholder: boolean }) => (theme: Theme) => css({
  color: isPlaceholder ? theme.textColors.disabled : undefined,
  height: 40,
});

const accordionItemStyle = css({
  textTransform: 'uppercase',
});

const accordionPanelStyle = css({
  padding: '0 0 24px',
});

const modalContentStyle = css({
  padding: 0,
});

const hiddenStyle = css({ display: 'none' });

const hideFillMetricsPanelStyle = (theme: Theme) => css({
  borderTop: `1px solid ${theme.borderColors.primary}`,
});

const hideFillMetricsToggleWrapperStyle = css({
  alignItems: 'center',
});

export const CustomizeMetricsComponent = ({
  addSelectionButtonText,
  columnDataSources,
  demographicsRegions = [],
  isLoading,
  isOpen,
  onClose,
  onSelectedDataChange,
  selectedData,
  selectedDataCaption,
  title,
  hideFillMetrics,
  onToggleHideFillMetrics,
  ...restProps
}: CustomizeMetricsProps) => {
  const [t] = useTranslation();

  const removeSelectedData = (index: number) => {
    const newData = [...selectedData];
    newData.splice(index, 1);

    onSelectedDataChange(newData);
  };

  const onToggleIncludeFillMetrics = useCallback((isOn: boolean) => {
    onToggleHideFillMetrics?.(!isOn);
  }, [onToggleHideFillMetrics]);

  return (
    <ModalComponent
      {...restProps}
      onClose={onClose}
      isOpen={isOpen}
      caption={title}
      contentStyle={modalContentStyle}
      additionalContent={isLoading ? (
        <OverlayLoaderComponent />
      ) : null}
    >
      <Accordion>
        <Accordion.Item
          isLocked
          open
        >
          <summary css={hiddenStyle} />
          <Accordion.Panel css={accordionPanelStyle}>
            <CustomizeMetricsSelectDataComponent
              addSelectionButtonText={addSelectionButtonText}
              columnDataSources={columnDataSources}
              demographicsRegions={demographicsRegions}
              isPopupOpen={isOpen}
              selectedData={selectedData}
              onSubmit={onSelectedDataChange}
            />
          </Accordion.Panel>
        </Accordion.Item>
        <Accordion.Item
          isLocked
          open
        >
          <Accordion.ItemHeading css={accordionItemStyle}>
            {selectedDataCaption}
          </Accordion.ItemHeading>
          <Accordion.Panel css={accordionPanelStyle}>
            <CustomizeMetricsResultsComponent
              selectedData={selectedData}
              onRemoveItem={index => removeSelectedData(index)}
            />
          </Accordion.Panel>
        </Accordion.Item>
        {!isNullsy(hideFillMetrics) && (
          <Accordion.Item
            isLocked
            open
          >
            <summary css={hiddenStyle} />
            <Accordion.Panel css={hideFillMetricsPanelStyle}>
              <Inline gap={8} css={hideFillMetricsToggleWrapperStyle}>
                <span>
                  {t('Include fill data in metrics panel')}
                </span>
                <ToggleComponent isOn={!hideFillMetrics} onChange={onToggleIncludeFillMetrics} />
              </Inline>
            </Accordion.Panel>
          </Accordion.Item>
        )}
      </Accordion>
    </ModalComponent>
  );
};
